import { selectPermission, selectUser } from "features/auth/currentUserSlice";
import { useParams } from "react-router-dom";
import {
    GuxIcon,
    GuxInlineAlert,
    GuxToggle,
} from "genesys-spark-components-react";
import { I18nComponent } from "react/utils/I18nComponent";
import navHelper from "services/navHelper";
import { ScorableMetaData, ScorableQuestionGroupList } from "./Scorable";
import {
    saveScorableByAgent,
    saveScorableBySupervisor,
    scorableSliceActions,
    selectAuthorizedAction,
    selectHasCalibrationPermissions,
    selectHaveDisputeDeclinePermission,
    selectHaveDisputePermission,
    selectHasEditScoreOrEditPermissions,
    selectIsCalibration,
    selectIsScorableComplete,
    selectReleaseDate,
    selectIsScorableInReview,
    selectScorable,
    submitScorableBySupervisor,
    selectIsScorableRetracted,
    selectHasScorableLoaded,
    selectIsEvaluationPendingOrInProgress,
    loadAiScoringAnswers,
} from "./scorableSlice";
import "./Evaluation.less";
import { selectFeatureToggle } from "features/featureToggles/featureToggleSlice";
import FeatureToggle from "enums/FeatureToggle";
import Footer from "features/qualityForm/formViewer/footer/Footer";
import { AdminComments, GeneralComments } from "components/Comments/Comments";
import EvaluationReviewCheck from "features/qualityForm/formViewer/footer/EvaluationReviewCheck";
import { useCallback, useContext, useEffect, useRef, useState } from "react";
import { ScoreOverview } from "./ScoreOverview";
import { SubmitActionOperations } from "features/qualityForm/formViewer/footer/SubmitActionOperations";
import ReleaseEvaluationSelector, {
    getCurrentReleaseDate,
    ReleaseDateSelectedType,
} from "features/qualityForm/formViewer/footer/releaseEvaluationTime/ReleaseEvaluationSelector";

import { AutomatedScoringStatus } from "./AutomatedScoring/AutomatedScoringStatus";
import { useAppDispatch, useAppSelector } from "app/hooks";
import {
    PreviewModeContext,
    ScorableValidationContext,
    UserModeContext,
    HasEditScoreOrEditPermissionsContext,
    RescoreModeContext,
    CheckboxModeContext,
} from "features/qualityForm/common/Contexts";
import strings from "./Evaluation.strings";
import { useStrings } from "features/i18n/useStrings";
import scrollToFirstError from "features/qualityForm/common/errorHandler";
import embeddingService from "services/embeddingService";
import toastService from "services/toastService";
import { useResolvedLocale } from "features/i18n/useLocale";
import { CalibrationSettings } from "features/qualityForm/formViewer/footer/CalibrationSettings";
import { CalibrationError } from "features/qualityForm/formViewer/footer/CalibrationSettings";
import { AlertMessage } from "./Evaluation/AlertMessage";
import { Header } from "./Header";
import { SideDrawer } from "./SideDrawer";
import { ReleaseDateOptions } from "enums/ReleaseDateOptions";
import { SideDrawerMode } from "enums/SideDrawerMode";
import { LoadingSpinner } from "react/components/LoadingSpinner";
import AuthorizedActions from "enums/AuthorizedActions";
import QualityFormPermission from "enums/qualityFormPermission";
import { useTimeZone } from "features/timezone/useTimeZone";
import windowEventService from "services/windowEventService";
import EvaluationSubmittedEventType from "enums/EvaluationSubmittedEventType";
import { selectIsSystemSubmitted } from "./scorableSlices/scorableCommons";
import { selectHasMultipleSelectQuestions } from "features/qualityForm/qualityFormValidations";
import {
    selectAutomatedEvaluationFeatureEnabled,
    selectIsAutomatedEvaluationForm,
} from "features/qualityForm/qualityFormSlices/qualityFormCommons";

export function EvaluationContextToggle({
    children,
    context,
}: {
    children?: string;
    context: "admin" | "agent";
}) {
    const { conversationId, evaluationId } = useParams();
    const isAssigneeFTEnabled = useAppSelector((state) =>
        selectFeatureToggle(
            state,
            FeatureToggle.EVALUATION_ASSIGNEE_FEATURE_TOGGLE_NAME
        )
    );
    const navUrl =
        context === "agent"
            ? `#/engage/admin/interactions/${conversationId}/qualitySummary/?evaluationId=${evaluationId}${
                  isAssigneeFTEnabled ? "&assignmentPanel=false" : ""
              }`
            : `#/engage/agent/interactions/${conversationId}/?evaluationId=${evaluationId}`;
    return (
        <a
            className="EvaluationContextToggle Clickable"
            href="#"
            onClick={() => navHelper.navigateTo(navUrl)}
        >
            <GuxIcon
                iconName={context === "agent" ? "unlock" : "lock"}
                decorative={true}
            ></GuxIcon>
            {children}
        </a>
    );
}

export function EvaluationContextTogglePreview({
    children,
    context,
    toggleAction,
}: {
    children?: string;
    context: "admin" | "agent";
    toggleAction: () => void;
}) {
    return (
        <div
            className="EvaluationContextToggle Clickable"
            data-testid="evaluation-context-toggle-preview"
            tabIndex={0}
            onClick={toggleAction}
            onKeyDown={(event) => {
                event.key === "Enter" && toggleAction();
            }}
        >
            <GuxIcon
                iconName={context === "agent" ? "unlock" : "lock"}
                decorative={true}
            ></GuxIcon>
            {children}
        </div>
    );
}

function BaseEvaluationContextSwitcher({
    context,
    toggleLink,
}: {
    context: "admin" | "agent";
    toggleLink: JSX.Element;
}) {
    const updatedContext = `${context}Evaluator`;

    return (
        <GuxInlineAlert
            accent="warning"
            data-testid="banner"
            className="evaluation-context-switcher"
        >
            <I18nComponent
                i18nKey={
                    `Scorable.contextWarning.${updatedContext}` as LocalizationKey
                }
                substitutions={{
                    toggleLink: toggleLink,
                }}
            />
        </GuxInlineAlert>
    );
}

export function EvaluationContextSwitcherPreview({
    context,
    toggleRoleView,
}: {
    context: "admin" | "agent";
    toggleRoleView: () => void;
}) {
    return (
        <BaseEvaluationContextSwitcher
            context={context}
            toggleLink={
                <EvaluationContextTogglePreview
                    context={context}
                    toggleAction={toggleRoleView}
                />
            }
        />
    );
}
export function EvaluationContextSwitcher({
    context,
    isInRescoreMode,
}: {
    context: "admin" | "agent";
    isInRescoreMode: boolean;
}) {
    const isLoggedInUserAgent = useAppSelector(
        (state) => selectScorable(state)?.agent?.id === selectUser(state)?.id
    );
    const canAddCalibration = useAppSelector((state) =>
        selectPermission(state, QualityFormPermission.QUALITY_CALIBRATION_ADD)
    );
    const canEditEvaluation = useAppSelector((state) =>
        selectAuthorizedAction(state, AuthorizedActions.EDIT)
    );
    const canEditEvaluationScore = useAppSelector((state) =>
        selectAuthorizedAction(state, AuthorizedActions.EDIT_SCORE)
    );
    const canAssignEvaluation = useAppSelector((state) =>
        selectAuthorizedAction(state, AuthorizedActions.ASSIGN)
    );
    const canReleaseEvaluation = useAppSelector((state) =>
        selectAuthorizedAction(state, AuthorizedActions.RELEASE)
    );
    const isCalibration = useAppSelector(selectIsCalibration);
    const isEvaluationPendingOrInProgress = useAppSelector(
        selectIsEvaluationPendingOrInProgress
    );
    const isEvaluationAssigneeFeatureEnabled = useAppSelector((state) => {
        return selectFeatureToggle(
            state,
            FeatureToggle.EVALUATION_ASSIGNEE_FEATURE_TOGGLE_NAME
        );
    });

    // TODO: When cleaning Assignee FT, remove this variable for conditions entirely
    // and just put the individual permission checks in the corresponding location.
    const canAssignOrReleaseEvaluation =
        isEvaluationAssigneeFeatureEnabled &&
        (canAssignEvaluation || canReleaseEvaluation);

    if (
        isCalibration ||
        isEvaluationPendingOrInProgress ||
        isInRescoreMode ||
        (context === "admin" && !isLoggedInUserAgent)
    ) {
        return null;
    }

    if (
        context === "agent" &&
        !canAddCalibration &&
        !canEditEvaluation &&
        !canEditEvaluationScore &&
        !canAssignOrReleaseEvaluation
    ) {
        return null;
    }

    return (
        <BaseEvaluationContextSwitcher
            context={context}
            toggleLink={<EvaluationContextToggle context={context} />}
        />
    );
}

export function MultipleSelectQuestionToggle({
    showMSQInCheckbox,
    setShowMSQInCheckbox,
}: {
    showMSQInCheckbox: boolean;
    setShowMSQInCheckbox: React.Dispatch<React.SetStateAction<boolean>>;
}) {
    const t = useStrings(strings);
    return (
        <div className="MultipleSelectQuestionToggle">
            <GuxToggle
                data-testid="multiple-select-question-toggle"
                label={t.labels.expandToggle}
                aria-label={t.labels.expandToggle}
                label-position="right"
                checked={showMSQInCheckbox}
                onCheck={() => {
                    setShowMSQInCheckbox(!showMSQInCheckbox);
                }}
            />
        </div>
    );
}

function redirectToNewEvaluation({
    conversationId,
    evaluationId,
}: {
    conversationId: string;
    evaluationId: string;
}) {
    const newEvaluationUrl = `#/engage/admin/interactions/${conversationId}/qualitySummary/?evaluationId=${evaluationId}`;
    embeddingService.requestNavigation(newEvaluationUrl);
}

export function Evaluation({
    context,
    sideDrawerMode,
    setSideDrawerMode,
}: {
    context: "admin" | "agent";
    sideDrawerMode: SideDrawerMode;
    setSideDrawerMode: React.Dispatch<React.SetStateAction<SideDrawerMode>>;
}) {
    const t = useStrings(strings);
    const isStatusFinished = useAppSelector(selectIsScorableComplete);
    const isStatusInReview = useAppSelector(selectIsScorableInReview);
    const isStatusRetracted = useAppSelector(selectIsScorableRetracted);
    const isStatusPendingOrInProgress = useAppSelector(
        selectIsEvaluationPendingOrInProgress
    );

    const [evaluationReviewed, setEvaluationReviewed] = useState(false);
    const [rescoreMode, setRescoreMode] = useState(false);
    const releaseDate = useAppSelector(selectReleaseDate);
    const [releaseDateData, setReleaseDateData] =
        useState<ReleaseDateSelectedType>({
            type: ReleaseDateOptions.NOW,
            time: releaseDate,
        });
    const dispatch = useAppDispatch();
    const isAdmin = context === "admin";
    const [isSubmitAttempted, setIsSubmitAttempted] = useState(false);
    const [useCalibrationScoringIndex, setUseCalibrationScoringIndex] =
        useState(false);
    const [recordCalibrationCreationError, setRecordCalibrationCreationError] =
        useState<CalibrationError>({ errorType: "" });
    const [checkboxMode, setCheckboxMode] = useState(false);

    function calibrationError(errorMessage: string) {
        if (
            errorMessage.startsWith("Evaluator (id '") &&
            errorMessage.endsWith(
                "') does not have evaluation edit or editScore permission"
            )
        ) {
            const id = errorMessage.split("'")[1];
            const message = {
                errorType: "PermissionsError",
                userId: id,
            } as CalibrationError;
            setRecordCalibrationCreationError(message);
        }
    }
    const ref = useRef<HTMLDivElement>(null);
    const locale = useResolvedLocale();
    const timeZone = useTimeZone();
    const options: Intl.DateTimeFormatOptions = {
        day: "numeric",
        month: "long",
        year: "numeric",
        hour: "numeric",
        minute: "numeric",
        timeZoneName: "short",
        timeZone: timeZone,
    };

    const isNotCalibration = !useAppSelector(selectIsCalibration);

    const hasCalibrationPermissions = useAppSelector(
        selectHasCalibrationPermissions
    );

    const { previewMode } = useContext(PreviewModeContext);
    const hasEditScoreOrEditPermissions =
        useAppSelector(selectHasEditScoreOrEditPermissions) || previewMode;

    const haveDisputeDeclinePermission = useAppSelector(
        selectHaveDisputeDeclinePermission
    );
    const haveDisputePermission = useAppSelector(selectHaveDisputePermission);

    const isEvaluationAssigneeFeatureEnabled = useAppSelector((state) => {
        return selectFeatureToggle(
            state,
            FeatureToggle.EVALUATION_ASSIGNEE_FEATURE_TOGGLE_NAME
        );
    });

    const isEvaluationOverviewDeprecationFeatureEnabled = useAppSelector(
        (state) => {
            return selectFeatureToggle(
                state,
                FeatureToggle.EVALUATION_OVERVIEW_DEPRECATION
            );
        }
    );

    const currentAssigneeId = useAppSelector(selectScorable)?.assignee?.id;

    const isSystemSubmitted = useAppSelector(selectIsSystemSubmitted);

    const showWEM742Footer =
        isEvaluationAssigneeFeatureEnabled && isAdmin && isNotCalibration;

    const isAutomatedEvaluationForm = useAppSelector(
        selectIsAutomatedEvaluationForm
    );
    const showCalibrationSettings =
        !isAutomatedEvaluationForm &&
        !isStatusInReview &&
        hasCalibrationPermissions;

    const isMultipleSelectFTEnabled = useAppSelector((state) => {
        return selectFeatureToggle(state, FeatureToggle.MULTIPLE_SELECT);
    });

    const hasMultipleSelectQuestion = useAppSelector(
        selectHasMultipleSelectQuestions
    );

    const showToggleForMultipleSelectQuestion =
        isAdmin &&
        isMultipleSelectFTEnabled &&
        (isStatusPendingOrInProgress || rescoreMode) &&
        hasMultipleSelectQuestion;

    const onSubmit = useCallback(
        (
            declinedReview?: boolean,
            dispute?: boolean,
            submitAndAssign?: boolean
        ) => {
            // If the user doesn't have editScore or edit permissions, they can't submit the evaluation
            // Unless they are declining a review, in which case they can if they have the decline permission.
            // Or if they are disputing a review, in which case they can if they have the dispute permission.
            if (
                (!hasEditScoreOrEditPermissions &&
                    !declinedReview &&
                    !dispute) ||
                (declinedReview && !haveDisputeDeclinePermission) ||
                (dispute && !haveDisputePermission)
            ) {
                toastService.notifyMinorAlert(
                    declinedReview
                        ? "evaluation.evaluatorsCantDeclineDisputedEvaluation"
                        : dispute
                        ? "evaluation.evaluatorsCantDisputeThisEvaluation"
                        : "evaluation.evaluatorsCantScoreThisEvaluation"
                );
                return;
            }
            // re-calculate releaseDate here:
            const updatedReleaseDate = getCurrentReleaseDate(
                releaseDateData,
                timeZone
            );
            dispatch(
                submitScorableBySupervisor({
                    currentAssignee: submitAndAssign
                        ? {
                              id: currentAssigneeId,
                          }
                        : undefined,
                    releaseDate: isNotCalibration
                        ? updatedReleaseDate
                        : undefined,
                    rescoreMode: rescoreMode,
                    declinedReview: declinedReview,
                    dispute: dispute,
                    isSubmitAndAssign: submitAndAssign,
                })
            ).then((e) => {
                const releaseDate = (e.payload ?? ({} as any)).releaseDate;
                if ("error" in e) {
                    setIsSubmitAttempted(true);
                    scrollToFirstError(ref);
                    // TODO: change how the errors are passed here so that error codes can be checked instead of relying on no payload being passed
                    if (e.payload === undefined) {
                        toastService.notifyMajorAlert(
                            declinedReview
                                ? "playback.evaluationDisputeDeclineRequest.error.default"
                                : dispute
                                ? "playback.evaluationDisputeRequest.error.default"
                                : "playback.evaluationSubmitRequest.error.default"
                        );
                    }
                } else {
                    if (declinedReview) {
                        toastService.notifyMessage(
                            "playback.evaluation.notification.evaluator.declineDispute.title",
                            {
                                bodyMessageKey:
                                    "playback.evaluation.notification.evaluator.declineDispute.msg",
                            },
                            false
                        );
                    } else if (dispute) {
                        toastService.notifyMessage(
                            "playback.evaluation.notification.evaluator.dispute.title",
                            {
                                bodyMessageKey:
                                    "playback.evaluation.notification.evaluator.dispute.msg",
                            },
                            false
                        );
                    } else {
                        isNotCalibration
                            ? toastService.notifyMessage(
                                  "playback.evaluation.notification.evaluator.submit.title",
                                  {
                                      bodyMessageKey:
                                          releaseDate !== undefined
                                              ? t.messages.submitEvaluation({
                                                    releaseDate:
                                                        releaseDate.toLocaleString(
                                                            locale,
                                                            options
                                                        ),
                                                })
                                              : t.messages.noReleaseDate,
                                      icon: "pc-icon-messages",
                                  },
                                  false
                              )
                            : toastService.notifyMessage(
                                  "playback.evaluation.notification.evaluator.submit.title",
                                  {
                                      bodyMessageKey:
                                          t.messages.submitCalibration,
                                      icon: "pc-icon-messages",
                                  },
                                  false
                              );
                    }

                    // prevents assignee sidedrawer from appearing on retracted evaluations
                    const payload: any = e.payload ?? {};
                    if (
                        submitAndAssign &&
                        window.location.hash.includes(payload.id)
                    ) {
                        setSideDrawerMode(SideDrawerMode.ASSIGN);
                    } else if (isEvaluationOverviewDeprecationFeatureEnabled) {
                        // if submitting after revision or submitting the inreview evaluation (not submit & assign)
                        // navigate to the new evaluation ID
                        if (!submitAndAssign) {
                            const conversationId = payload?.conversation?.id;
                            const evaluationId = payload?.id;
                            const shouldRedirect =
                                conversationId &&
                                evaluationId &&
                                !window.location.hash.includes(evaluationId);
                            if (shouldRedirect) {
                                redirectToNewEvaluation({
                                    conversationId: conversationId,
                                    evaluationId: evaluationId,
                                });
                            }
                        }
                    } else {
                        if (!submitAndAssign && !dispute && !declinedReview) {
                            embeddingService.requestNavigation(
                                "/engage/dashboard/qualityEvaluator"
                            );
                        }
                    }
                    //updating rescore mode in case navigation fails
                    setRescoreMode(false);
                }
            });
        },
        [
            dispatch,
            releaseDateData,
            isNotCalibration,
            haveDisputeDeclinePermission,
            haveDisputePermission,
            rescoreMode,
            timeZone,
            locale,
            options,
        ]
    );
    const onDisputeDecline = onSubmit.bind(null, true);
    const onDispute = onSubmit.bind(null, false, true);
    const onSubmitAndAssign = onSubmit.bind(null, false, false, true);
    const onSave = useCallback(() => {
        if (!hasEditScoreOrEditPermissions) {
            toastService.notifyMinorAlert(
                "evaluation.evaluatorsCantScoreThisEvaluation"
            );
            return;
        }
        dispatch(saveScorableBySupervisor()).then((e) => {
            if (!("error" in e)) {
                toastService.notifyMessage(
                    "playback.evaluation.notification.evaluator.save.title",
                    {
                        bodyMessageKey:
                            "playback.evaluation.notification.evaluator.save.msg",
                        icon: "pc-icon-messages",
                    },
                    false
                );

                if (isEvaluationOverviewDeprecationFeatureEnabled) {
                    const payload: any = e.payload ?? {};
                    // If we save any finished/in review evaluation, then we will redirect to its In Progress evaluation
                    const conversationId = payload?.conversation?.id;
                    const evaluationId = payload?.id;
                    const shouldRedirect =
                        conversationId &&
                        evaluationId &&
                        !window.location.hash.includes(evaluationId);
                    if (shouldRedirect) {
                        redirectToNewEvaluation({
                            conversationId: conversationId,
                            evaluationId: evaluationId,
                        });
                    }
                }
            } else {
                toastService.notifyMajorAlert(
                    "playback.evaluationSaveRequest.error.default"
                );
            }
            //updating rescore mode in case navigation fails
            setRescoreMode(false);
        });
    }, [dispatch]);
    const onSaveByAgent = useCallback(
        (dispute?: boolean) => {
            dispatch(
                saveScorableByAgent({
                    evaluationReviewed,
                    dispute: dispute || false,
                })
            ).then((e) => {
                if ("error" in e) {
                    if (dispute) {
                        toastService.notifyMajorAlert(
                            "playback.evaluationDisputeRequest.error.default"
                        );
                    } else {
                        setIsSubmitAttempted(true);
                        scrollToFirstError(ref);
                        toastService.notifyMajorAlert(
                            "playback.evaluation.notification.agent.fail.msg"
                        );
                    }
                } else {
                    if (dispute) {
                        toastService.notifyMessage(
                            "playback.evaluation.notification.agent.dispute.title",
                            {
                                bodyMessageKey:
                                    "playback.evaluation.notification.agent.dispute.msg",
                            },
                            false
                        );
                    } else {
                        toastService.notifyMessage(
                            "playback.evaluation.notification.agent.save.title",
                            {
                                bodyMessageKey:
                                    "playback.evaluation.notification.agent.save.msg",
                                icon: "pc-icon-messages",
                            },
                            false
                        );
                    }
                    embeddingService.requestNavigation(
                        "#/analytics/agents/evaluations/me"
                    );
                }
            });
        },
        [dispatch, evaluationReviewed]
    );
    const onDisputeByAgent = onSaveByAgent.bind(null, true);
    useEffect(() => {
        if (isSubmitAttempted) {
            scrollToFirstError(ref);
        }
    }, [isSubmitAttempted]);
    const isEvaluationComplete = isStatusFinished && !rescoreMode;

    useEffect(() => {
        if (sideDrawerMode === SideDrawerMode.OFF) {
            dispatch(scorableSliceActions.setUpdateEvaluationError({}));
        }
    }, [sideDrawerMode, dispatch]);

    const hasScorableLoaded = useAppSelector(selectHasScorableLoaded);
    const isAutomatedEvaluationCompletionFeatureEnabled = useAppSelector(
        selectAutomatedEvaluationFeatureEnabled
    );
    const fetchLatestAiScoringAnswer = useCallback(
        (event: MessageEvent) => {
            const messageData = event.data;
            if (
                event.source === window.parent &&
                messageData.evaluationNotificationEventType &&
                messageData.evaluationNotificationEventType ===
                    EvaluationSubmittedEventType.AI_SCORED
            ) {
                dispatch(loadAiScoringAnswers()).then((result) => {
                    // Show notification only when dispatch completes successfully
                    if (
                        !("error" in result) &&
                        isAutomatedEvaluationCompletionFeatureEnabled
                    ) {
                        toastService.notifyMessage(
                            t.messages.evaluationScoringComplete.title,
                            {
                                bodyMessageKey:
                                    t.messages.evaluationScoringComplete.body,
                                icon: "pc-icon-messages",
                            },
                            false
                        );
                    }
                });
            }
        },
        [dispatch, t, isAutomatedEvaluationCompletionFeatureEnabled]
    );

    useEffect(() => {
        const aiScoredEventListener = "ai-scored-event-listener";
        windowEventService.registerEvent(
            aiScoredEventListener,
            "message",
            fetchLatestAiScoringAnswer
        );
        return () => {
            windowEventService.removeEvent(aiScoredEventListener, "message");
        };
    }, [fetchLatestAiScoringAnswer]);

    return hasScorableLoaded ? (
        <div className="Evaluation" data-testid="Evaluation" ref={ref}>
            <Header isAdmin={isAdmin} />
            <HasEditScoreOrEditPermissionsContext.Provider
                value={{ hasEditScoreOrEditPermissions }}
            >
                {sideDrawerMode === SideDrawerMode.ASSIGN ? (
                    <SideDrawer
                        setSideDrawerMode={setSideDrawerMode}
                    ></SideDrawer>
                ) : (
                    <>
                        {isAdmin && <AutomatedScoringStatus />}
                        <ScorableMetaData
                            context={isEvaluationComplete ? context : undefined}
                        />
                        <AlertMessage isAdmin={isAdmin}>
                            <EvaluationContextSwitcher
                                context={context}
                                isInRescoreMode={rescoreMode}
                            />
                        </AlertMessage>
                        {showToggleForMultipleSelectQuestion && (
                            <MultipleSelectQuestionToggle
                                showMSQInCheckbox={checkboxMode}
                                setShowMSQInCheckbox={setCheckboxMode}
                            />
                        )}
                        <UserModeContext.Provider value={{ isAdmin }}>
                            <ScorableValidationContext.Provider
                                value={{
                                    showValidationErrors: isSubmitAttempted,
                                }}
                            >
                                {showToggleForMultipleSelectQuestion ? (
                                    <CheckboxModeContext.Provider
                                        value={{ checkboxMode }}
                                    >
                                        <ScorableQuestionGroupList
                                            rescoringEvaluation={rescoreMode}
                                        />
                                    </CheckboxModeContext.Provider>
                                ) : (
                                    <ScorableQuestionGroupList
                                        rescoringEvaluation={rescoreMode}
                                    />
                                )}
                            </ScorableValidationContext.Provider>
                        </UserModeContext.Provider>
                        {!isSystemSubmitted &&
                            (showWEM742Footer ? (
                                <div className="WEM742Footer">
                                    <RescoreModeContext.Provider
                                        value={{ rescoreMode }}
                                    >
                                        <AdminComments />
                                    </RescoreModeContext.Provider>
                                    {!isStatusRetracted && (
                                        <ReleaseEvaluationSelector
                                            setReleaseDateData={(d) =>
                                                setReleaseDateData(d)
                                            }
                                            releaseDateData={releaseDateData}
                                            rescoreMode={rescoreMode}
                                            showSubmit={isEvaluationComplete}
                                        />
                                    )}
                                    {!isStatusRetracted &&
                                        isEvaluationComplete && (
                                            <div className="evaluationToggles">
                                                <EvaluationReviewCheck
                                                    isAdmin={isAdmin}
                                                    onChange={() =>
                                                        setEvaluationReviewed(
                                                            !evaluationReviewed
                                                        )
                                                    }
                                                    evaluationReviewed={
                                                        evaluationReviewed
                                                    }
                                                />
                                                {showCalibrationSettings && (
                                                    <CalibrationSettings
                                                        calibrationMode={
                                                            useCalibrationScoringIndex
                                                        }
                                                        recordCalibrationCreationError={
                                                            recordCalibrationCreationError
                                                        }
                                                        setRecordCalibrationCreationError={
                                                            setRecordCalibrationCreationError
                                                        }
                                                        toggleCalibrationMode={() => {
                                                            setUseCalibrationScoringIndex(
                                                                !useCalibrationScoringIndex
                                                            );
                                                        }}
                                                    ></CalibrationSettings>
                                                )}
                                            </div>
                                        )}
                                    {!isStatusRetracted ? (
                                        <SubmitActionOperations
                                            isCompleted={isEvaluationComplete}
                                            rescoreMode={rescoreMode}
                                            calibrationMode={
                                                useCalibrationScoringIndex
                                            }
                                            toggleCalibrationMode={() =>
                                                setUseCalibrationScoringIndex(
                                                    !useCalibrationScoringIndex
                                                )
                                            }
                                            toggleRescoreMode={() =>
                                                setRescoreMode(!rescoreMode)
                                            }
                                            isAdmin={isAdmin}
                                            attemptSubmit={onSubmit}
                                            attemptSubmitAndAssign={
                                                onSubmitAndAssign
                                            }
                                            attemptSaveByAgent={
                                                !evaluationReviewed
                                                    ? undefined
                                                    : onSaveByAgent
                                            }
                                            attemptSave={onSave}
                                            attemptDispute={
                                                isStatusFinished &&
                                                !isStatusInReview
                                                    ? onDispute
                                                    : undefined
                                            }
                                            attemptDisputeByAgent={
                                                evaluationReviewed
                                                    ? onDisputeByAgent
                                                    : undefined
                                            }
                                            attemptDisputeDecline={
                                                isStatusInReview
                                                    ? onDisputeDecline
                                                    : undefined
                                            }
                                            calibrationError={(error) =>
                                                calibrationError(error)
                                            }
                                            setSideDrawerMode={
                                                setSideDrawerMode
                                            }
                                            releaseDateData={releaseDateData}
                                        />
                                    ) : (
                                        <></>
                                    )}
                                </div>
                            ) : (
                                <Footer>
                                    <GeneralComments
                                        context={context}
                                        rescoreMode={rescoreMode}
                                    />
                                    {isNotCalibration &&
                                        !isStatusRetracted &&
                                        (isEvaluationComplete ? (
                                            <div className="evaluationToggles">
                                                <EvaluationReviewCheck
                                                    isAdmin={isAdmin}
                                                    onChange={() =>
                                                        setEvaluationReviewed(
                                                            !evaluationReviewed
                                                        )
                                                    }
                                                    evaluationReviewed={
                                                        evaluationReviewed
                                                    }
                                                />
                                                {isAdmin &&
                                                    showCalibrationSettings && (
                                                        <CalibrationSettings
                                                            calibrationMode={
                                                                useCalibrationScoringIndex
                                                            }
                                                            recordCalibrationCreationError={
                                                                recordCalibrationCreationError
                                                            }
                                                            setRecordCalibrationCreationError={
                                                                setRecordCalibrationCreationError
                                                            }
                                                            toggleCalibrationMode={() => {
                                                                setUseCalibrationScoringIndex(
                                                                    !useCalibrationScoringIndex
                                                                );
                                                            }}
                                                        ></CalibrationSettings>
                                                    )}
                                            </div>
                                        ) : (
                                            <ReleaseEvaluationSelector
                                                setReleaseDateData={(d) =>
                                                    setReleaseDateData(d)
                                                }
                                                releaseDateData={
                                                    releaseDateData
                                                }
                                                rescoreMode={rescoreMode}
                                                showSubmit={
                                                    isEvaluationComplete
                                                }
                                            />
                                        ))}
                                    {!isStatusRetracted && (
                                        <SubmitActionOperations
                                            isCompleted={isEvaluationComplete}
                                            rescoreMode={rescoreMode}
                                            calibrationMode={
                                                useCalibrationScoringIndex
                                            }
                                            toggleCalibrationMode={() =>
                                                setUseCalibrationScoringIndex(
                                                    !useCalibrationScoringIndex
                                                )
                                            }
                                            toggleRescoreMode={() =>
                                                setRescoreMode(!rescoreMode)
                                            }
                                            isAdmin={isAdmin}
                                            attemptSubmit={onSubmit}
                                            attemptSaveByAgent={
                                                !evaluationReviewed
                                                    ? undefined
                                                    : onSaveByAgent
                                            }
                                            attemptDispute={
                                                isStatusFinished &&
                                                !isStatusInReview
                                                    ? onDispute
                                                    : undefined
                                            }
                                            attemptDisputeByAgent={
                                                evaluationReviewed
                                                    ? onDisputeByAgent
                                                    : undefined
                                            }
                                            attemptDisputeDecline={
                                                isStatusInReview
                                                    ? onDisputeDecline
                                                    : undefined
                                            }
                                            attemptSave={onSave}
                                            calibrationError={(error) =>
                                                calibrationError(error)
                                            }
                                            setSideDrawerMode={
                                                setSideDrawerMode
                                            }
                                            releaseDateData={releaseDateData}
                                        />
                                    )}
                                </Footer>
                            ))}
                        {isEvaluationComplete && <ScoreOverview />}
                    </>
                )}
            </HasEditScoreOrEditPermissionsContext.Provider>
        </div>
    ) : (
        <LoadingSpinner />
    );
}
